import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, ArrowRight, ArrowLeft, ShieldAlert } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMG =
  "https://images.unsplash.com/photo-1592317460245-765f641e122b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function SetPassword() {
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const emailParam = params.get("email") || "";
  const [form, setForm] = useState({
    email: emailParam,
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState(null); // null = checking, true/false = result
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!emailParam) {
        if (active) setEligible(false);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/auth/setup-status`, { params: { email: emailParam } });
        if (active) setEligible(!!data.needs_setup);
      } catch (e) {
        if (active) setEligible(false);
      }
    };
    check();
    return () => { active = false; };
  }, [emailParam]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/set-password`, {
        email: form.email,
        current_password: form.current_password,
        new_password: form.new_password,
      });
      adoptSession(data.token, data.user);
      toast.success("Password set. You're all signed in.");
      navigate(data.user?.role === "superadmin" ? "/admin" : "/analytics");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not set password.");
      if (err.response?.status === 403) setEligible(false);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-base-bg/25 py-3 text-base-bg placeholder:text-base-bg/40 focus:border-sand focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-emerald-950">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={IMG} alt="Irish landscape" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-emerald-950/55" aria-hidden="true" />
        <div className="absolute bottom-12 left-10 right-10">
          <p className="font-serif text-4xl text-base-bg leading-tight">TILT Ventures</p>
          <p className="mt-3 text-base-bg/70 max-w-sm">Set a password only you know to secure your account.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
          data-testid="set-password-card"
        >
          <button
            data-testid="setpw-home-button"
            onClick={() => navigate("/")}
            className="mb-8 inline-flex items-center gap-2 text-base-bg/60 text-[0.72rem] uppercase tracking-[0.18em] hover:text-base-bg transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </button>

          {eligible === null && (
            <p data-testid="setpw-checking" className="text-base-bg/60 text-sm">Checking your invitation…</p>
          )}

          {eligible === false && (
            <div data-testid="setpw-invalid">
              <div className="flex items-center gap-2 text-sand text-[0.72rem] uppercase tracking-[0.22em] mb-6">
                <ShieldAlert className="h-3.5 w-3.5" /> Link unavailable
              </div>
              <h1 className="font-serif text-4xl text-base-bg leading-none">This link is no longer active</h1>
              <p className="mt-4 text-base-bg/60 text-sm">
                This set-password link has already been used or is not valid. If you've already set your
                password, please sign in. Otherwise, ask your administrator to re-send an invitation.
              </p>
              <button
                data-testid="setpw-invalid-goto-login"
                onClick={() => navigate("/login")}
                className="group mt-10 inline-flex items-center gap-3 bg-base-bg px-8 py-4 text-sm uppercase tracking-[0.18em] text-emerald-950"
              >
                Go to sign in
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          )}

          {eligible === true && (
          <>
          <div className="flex items-center gap-2 text-sand text-[0.72rem] uppercase tracking-[0.22em] mb-6">
            <KeyRound className="h-3.5 w-3.5" /> Set your password
          </div>
          <h1 className="font-serif text-4xl text-base-bg leading-none">Choose a password</h1>
          <p className="mt-4 text-base-bg/60 text-sm">
            Enter the temporary password from your invitation email, then set your own.
          </p>

          <form onSubmit={submit} data-testid="set-password-form" className="mt-10 space-y-8">
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Email</label>
              <input data-testid="setpw-email" type="email" value={form.email} onChange={update("email")} placeholder="you@tiltventures.ie" className={field} />
            </div>
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Temporary password</label>
              <input data-testid="setpw-current" type="password" value={form.current_password} onChange={update("current_password")} placeholder="From your email" className={field} />
            </div>
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">New password</label>
              <input data-testid="setpw-new" type="password" value={form.new_password} onChange={update("new_password")} placeholder="Min 8 characters" className={field} />
            </div>
            <div>
              <label className="text-[0.7rem] uppercase tracking-[0.18em] text-base-bg/50">Confirm new password</label>
              <input data-testid="setpw-confirm" type="password" value={form.confirm} onChange={update("confirm")} placeholder="Re-enter new password" className={field} />
            </div>
            <button
              data-testid="setpw-submit-button"
              type="submit"
              disabled={loading}
              className="group inline-flex w-full items-center justify-center gap-3 bg-base-bg px-8 py-4 text-sm uppercase tracking-[0.18em] text-emerald-950 disabled:opacity-60"
            >
              {loading ? "Saving..." : "Set password & sign in"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>

          <button data-testid="setpw-goto-login" onClick={() => navigate("/login")} className="mt-8 text-sm text-base-bg/50 hover:text-base-bg transition-colors">
            Already set your password? Sign in →
          </button>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
